import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2909" data-mp="3jf6le6i1hzon">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-2910" data-mp="zvwv3kzhy8g">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-2911" data-mp="3scyzj7c0z1ag">
        {children}
      </div>
    </motion.section>;
}
export function Terms() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2912" data-mp="2zol2atsrg8zy">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-2913" data-mp="qoso0sc3hduf">
        <div className="max-w-3xl mx-auto" data-id="element-2914" data-mp="vmu2ezyksozk">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2915" data-mp="vlj2xcc5y3e9">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2916" data-mp="10fex03pb3645">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-2917" data-mp="co9fzl7mq7s9">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-2918" data-mp="lpg0s6hba4rb">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2919" data-mp="1x3bct6cx5xeb">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2920" data-mp="3u4z2sc9jq3xi">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-2921" data-mp="6yzukg71y9sq">
            These Terms of Service ("Terms") govern your access to and use of
            the Structured Nutrition website, products, and services
            ("Services"). By using our Services, you agree to these Terms. If
            you do not agree, please do not use the Services.
          </motion.p>

          {/* DISCLAIMERS — two clearly separated callouts */}
          <motion.div variants={fadeUp} className="mb-6 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-2922" data-mp="3fejj51ws341g">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-2923" data-mp="2mpkwbrhpndpt">
              General Content — Educational Only
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-2924" data-mp="t9re3qlql8t8">
              The general content published on this website, in our books,
              guides, downloads, emails, and social media is for educational and
              informational purposes only. Because we don't know your individual
              medical history, medications, or circumstances, this content is
              not personalized nutrition advice and is not a substitute for
              consulting a licensed healthcare provider. Always speak with your
              prescribing physician before making changes to your medication,
              nutrition, or exercise plan — especially while taking GLP-1
              medications. Never disregard professional medical advice or delay
              seeking it because of something you read here.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mb-12 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-2925" data-mp="g2alixytky1k">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-2926" data-mp="72f62gy8egvq">
              Supplements — Educational Use Only
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-2927" data-mp="zo70171n1ftt">
              Products sold by Structured Nutrition are dietary supplements and
              educational materials. They are not intended to diagnose, treat,
              cure, or prevent any disease, and nothing on this store replaces
              the care of your physician or other healthcare providers. Talk to
              your provider before starting any supplement, especially if you
              are pregnant, nursing, managing a medical condition, or taking
              prescription medication.
            </p>
          </motion.div>

          <Section title="What We Sell" data-id="element-2928" data-mp="3aclm60dxkcbs">
            <p data-id="element-2929" data-mp="3abk2x8mrgjiz">
              Structured Nutrition is an online store offering dietary
              supplements and digital products (e-books and downloadable
              guides). Product descriptions, availability, and pricing may
              change without notice.
            </p>
          </Section>

          <Section title="Accounts" data-id="element-2930" data-mp="3o25lew8ayrgc">
            <p data-id="element-2931" data-mp="3t2tk842rwcl4">
              To access certain features, you'll need to create an account. You
              agree to:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2932" data-mp="1vh2nnxz9x4o">
              <li data-id="element-2933" data-mp="1yp236ji46qfn">Provide accurate and complete information</li>
              <li data-id="element-2934" data-mp="1yqmxq94fi9di">Keep your login credentials confidential</li>
              <li data-id="element-2935" data-mp="23owugo5sxi18">
                Be responsible for all activity that happens under your account
              </li>
              <li data-id="element-2936" data-mp="1vnpjhxhlb44t">
                Notify us immediately of any unauthorized use of your account
              </li>
            </ul>
            <p data-id="element-2937" data-mp="1qqgy7o15ajzc">
              We may suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="Purchases and Payment" data-id="element-2938" data-mp="20lr2yjixh5gt">
            <p data-id="element-2939" data-mp="20kz675dlh1oy">
              Payments are processed securely by Stripe. By purchasing a product
              or service, you authorize us to charge the payment method you
              provide for the full purchase amount, including any applicable
              taxes.
            </p>
            <p data-id="element-2940" data-mp="2obbmb3izu4qm">
              All prices are listed in U.S. dollars unless otherwise noted. We
              reserve the right to change pricing at any time.
            </p>
          </Section>

          <Section title="Refunds" data-id="element-2941" data-mp="2xdcevn3a82ly">
            <p data-id="element-2942" data-mp="2xa6ps7unl0q8">
              Digital products (e-books, guides, downloads) are non-refundable
              once accessed or downloaded. Coaching services may be eligible for
              a refund within 7 days of purchase if no session has yet been
              delivered — contact us to request one. Refunds for ongoing
              programs are evaluated case-by-case.
            </p>
          </Section>

          <Section title="Intellectual Property" data-id="element-2943" data-mp="37xmzffwzk06c">
            <p data-id="element-2944" data-mp="37w3f9e87l4za">
              All content on this site — including text, graphics, logos, books,
              guides, and training materials — is owned by Structured Nutrition
              or licensed to us and is protected by copyright and other
              intellectual property laws.
            </p>
            <p data-id="element-2945" data-mp="2k9n4s4wjzpmh">
              You may not copy, distribute, resell, or create derivative works
              from our content without our prior written permission. Purchasing
              a digital product grants you a personal, non-transferable license
              to use it.
            </p>
          </Section>

          <Section title="Acceptable Use" data-id="element-2946" data-mp="2fb23s39h91wz">
            <p data-id="element-2947" data-mp="2ur8w6qrqtjsh">By using our Services, you agree NOT to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2948" data-mp="2uompetcof8bb">
              <li data-id="element-2949" data-mp="2psfgqsswbipo">
                Use the Services for any unlawful, harmful, or fraudulent
                purpose
              </li>
              <li data-id="element-2950" data-mp="2ls5d3mskt4dz">
                Share, redistribute, or resell our digital products or store
                materials
              </li>
              <li data-id="element-2951" data-mp="2bsejrofr4vfh">
                Attempt to gain unauthorized access to our systems or other
                users' accounts
              </li>
              <li data-id="element-2952" data-mp="26zbkr0s7gtrw">Harass, abuse, or threaten our team or other users</li>
              <li data-id="element-2953" data-mp="222uwm6l0afet">
                Use our content to provide nutrition or medical advice to others
                as a substitute for your own credentialed practice
              </li>
            </ul>
          </Section>

          <Section title="Disclaimer of Warranties" data-id="element-2954" data-mp="1t3pxoz5j8k24">
            <p data-id="element-2955" data-mp="1o2ag5h2kcvoh">
              Our Services are provided "as is" and "as available," without
              warranties of any kind, either express or implied. We do not
              guarantee any specific health, weight, or fitness outcomes from
              using our Services.
            </p>
          </Section>

          <Section title="Limitation of Liability" data-id="element-2956" data-mp="dk0gbaextlqz">
            <p data-id="element-2957" data-mp="hkapsx1xuual">
              To the fullest extent permitted by law, Structured Nutrition and
              its team are not liable for any indirect, incidental, special,
              consequential, or punitive damages arising from your use of the
              Services — including but not limited to health outcomes, lost
              profits, or data loss.
            </p>
            <p data-id="element-2958" data-mp="3pxzso28kjse6">
              Our total liability for any claim arising from these Terms or your
              use of the Services is limited to the amount you paid us in the 12
              months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="Indemnification" data-id="element-2959" data-mp="328owyz62so6w">
            <p data-id="element-2960" data-mp="15wlib92p7qsr">
              You agree to defend, indemnify, and hold harmless Structured
              Nutrition from any claims, damages, or expenses (including
              reasonable attorneys' fees) arising out of your violation of these
              Terms or your misuse of the Services.
            </p>
          </Section>

          <Section title="Governing Law" data-id="element-2961" data-mp="1jutcj98coe08">
            <p data-id="element-2962" data-mp="1jtql907489js">
              These Terms are governed by the laws of the State of [Your State],
              without regard to conflict-of-laws principles. Any dispute will be
              resolved in the state or federal courts located in [Your
              County/State].
            </p>
          </Section>

          <Section title="Changes to These Terms" data-id="element-2963" data-mp="10x8p86l8hnhq">
            <p data-id="element-2964" data-mp="10yti7rrwgydh">
              We may update these Terms from time to time. The "Last updated"
              date at the top reflects the most recent revision. Continued use
              of the Services after changes constitutes acceptance of the
              updated Terms.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2965" data-mp="wnazx8d59zl">
            <p data-id="element-2966" data-mp="3s3zkrr6x2l5h">
              Questions about these Terms? Reach out through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2967" data-mp="3n4mv6yonuzz7">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-2968" data-mp="39d8cksyo10so">
            This document is provided as a general template and does not
            constitute legal advice. Bracketed placeholders (such as state and
            venue) should be filled in, and the full document should be reviewed
            with an attorney before being relied upon.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}